import { useMutation, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import type { RespondedType, ResponseError } from '@/models/response';
import { addTranslateToast } from '@/stores/toast';
import { fetcher } from '@/utils/fetcher';
import { generateTagMemberQueryOptions } from './use-tag-members';
import { tagListQueryOptions } from './use-tags';

export default function useRemoveTagMember(tagId: number) {
  const queryClient = useQueryClient();

  return useMutation<RespondedType, ResponseError, number>({
    mutationFn: (params) =>
      fetcher(
        '/RemoveTagMember',
        {
          TagId: tagId,
          MemberId: params,
        },
        { validResponseCodes: [0] },
      ),
    onSuccess: async (_, params) => {
      addTranslateToast({
        type: 'success',
        message: 'common.delete-success',
      });
      queryClient.setQueryData(tagListQueryOptions.queryKey, (state) =>
        produce(state, (draft) => {
          if (!draft?.Data) return draft;

          draft.Data.Tags = draft.Data.Tags.map((t) =>
            t.Id === tagId
              ? {
                  ...t,
                  TagMember: t.TagMember.filter((tm) => tm.Id !== params),
                  TagCount: t.TagMember.length - 1,
                }
              : t,
          );

          return draft;
        }),
      );
      queryClient.setQueryData(generateTagMemberQueryOptions(tagId).queryKey, (state) =>
        state ? state.filter((member) => member.MemberId !== params) : state,
      );
    },
  });
}
